import { MagnifyingGlassIcon } from "@heroicons/react/24/outline"
import {
  Button,
  DashboardContainer,
  TopActionBar,
  H6,
} from "../../../../components"
import { Input, Select } from "antd"
import type { TableColumnsType } from "antd"
import { useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { Person } from "./interface/confirmation"
import { useAppNotificationStore } from "stores"
import CustomModal from "components/new-stuff/ui/modals/CustomModal"
import PersonAvatar from "components/new-stuff/ui/PersonAvatar"
import {
  ADMIN_PEOPLE_ENDPOINTS,
  adminPeopleService,
} from "services/adminPeopleService"
import { COMPANY_ENDPOINTS, companyService } from "services/companyService"
import { onQueryError } from "utils/utils"
import { useBackendTableQuery } from "pages/dashboard/performance/components/useBackendTableQuery"
import ConfirmationReviewDrawer from "./ConfirmationReviewDrawer"
import { AppTable, TableActionSelect } from "components/new-stuff/app-table"

interface IDepartmentRes {
  id: number
  text: string
}

const Confirmation: React.FC<{}> = () => {
  const { toast } = useAppNotificationStore()
  const queryClient = useQueryClient()
  const [showConfirmBtn, setShowConfirmBtn] = useState(false)
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false)
  const [openReviewDrawer, setOpenReviewDrawer] = useState(false)
  const [selectedPersons, setSelectedPersons] = useState<React.Key[]>([])
  const [selectedPeople, setSelectedPeople] = useState<Person[]>([])
  const [activeConfirmEmp, setActiveConfirmEmp] = useState({
    fullName: "",
    person_id: "",
  })

  const clearSelection = () => {
    setSelectedPersons([])
    setSelectedPeople([])
    setShowConfirmBtn(false)
  }

  const {
    isLoading: isLoadingConfirmation,
    data: confirmationRes,
    isPreviousData,
    search,
    handleSearchChange,
    tablePagination,
    patchQueryExtraParams,
  } = useBackendTableQuery<IPaginatedRes<Person>>({
    queryKeyBase: [ADMIN_PEOPLE_ENDPOINTS.SELECT_FOR_CONFIRMATION],
    queryFn: (params) => adminPeopleService.selectForConfirmation(params),
    getTotal: (data) => data?.data?.totalDocs ?? 0,
    searchParamName: "searchParams",
    initialPageSize: 20,
  })

  const { isLoading: isLoadingConfirmSelected, mutate: confirmSelected } =
    useMutation({
      mutationFn: (body: Record<string, any>) =>
        adminPeopleService.confirmEmployee(body),
      onSuccess: (data) => {
        setOpenConfirmationModal(false)
        setOpenReviewDrawer(false)
        clearSelection()
        data?.message && toast.success(data.message)
        queryClient.invalidateQueries({
          queryKey: [ADMIN_PEOPLE_ENDPOINTS.SELECT_FOR_CONFIRMATION],
        })
        setActiveConfirmEmp({
          fullName: "",
          person_id: "",
        })
      },
      onError: (error) => {
        setOpenConfirmationModal(false)
        setOpenReviewDrawer(false)
        setActiveConfirmEmp({
          fullName: "",
          person_id: "",
        })
        onQueryError(error)
      },
    })

  const { isLoading: isDepartmentDataLoading, data: departmentRes } = useQuery<
    IBaseRes<IDepartmentRes[]>
  >({
    queryKey: [COMPANY_ENDPOINTS.GET_DEPARTMENT_PEOPLE],
    queryFn: () => companyService.getDepartmentPeople({ q: "", page: "" }),
    onError: onQueryError,
  })
  const departmentData = departmentRes?.data

  const TableColumns: TableColumnsType<Person> = [
    {
      title: "Name",
      render: (_, record) => (
        <div className="flex items-center gap-2">
          <span className="shrink-0">
            <PersonAvatar
              className="w-7 h-7 rounded-full shrink-0"
              src={record.avatar_url}
              alt=""
            />
          </span>
          <span>
            <span className="uppercase font-semibold text-neutral500">
              {record.first_name}
            </span>{" "}
            {record.middle_name ? ` ${record.middle_name} ` : " "}{" "}
            {record.last_name}
          </span>
        </div>
      ),
    },
    {
      title: "Email address",
      dataIndex: "email",
    },
    {
      title: "Department",
      dataIndex: "department",
      render: (_, record) => (
        <div className="flex flex-col gap-2">
          <span>{record.department}</span>
          <span className=" text-[11px] !text-neutral200  font-light">
            {record.employee_title}
          </span>{" "}
        </div>
      ),
    },
    {
      title: "Confirmation",
      dataIndex: "confirmation_days_late",
      render: (value: number) => `${value} days late`,
    },
    {
      title: "Phone",
      dataIndex: "mobile_phone",
    },
    {
      title: "Status",
      dataIndex: "person_status",
      render: (_, record) => (
        <span
          className={`text-sm font-medium py-1 block text-center w-[80px] rounded-[4px]
                    ${
                      record.person_status_id === 1
                        ? "bg-success100 text-success800"
                        : ""
                    }
                    ${
                      record.person_status_id === 2
                        ? "bg-neutral40 text-neutral500"
                        : ""
                    }
                    ${
                      record.person_status_id === 3
                        ? "bg-[#FEE3B1] text-[#E69702]"
                        : ""
                    }
                    ${
                      record.person_status_id === 4
                        ? "bg-danger50 text-neutral0"
                        : ""
                    }
                    ${
                      record.person_status_id === 5
                        ? "bg-danger50 text-neutral0"
                        : ""
                    }
                    `}
        >
          {record.person_status}
        </span>
      ),
    },

    {
      title: "",
      key: "action",
      render: (_, record) => (
        <TableActionSelect
          items={actionItems({
            personKey: record.person_key,
            personId: record.person_id,
            name: ` ${record.first_name} ${record.last_name}`,
          })}
        />
      ),
    },
  ]

  //  three dots on each row
  const actionItems = (data: {
    personKey: string
    personId: number
    name: string
  }) => [
    {
      key: "1",
      label: (
        <span
          onClick={() => {
            setOpenConfirmationModal(true)
            setActiveConfirmEmp({
              fullName: data.name,
              person_id: String(data.personId),
            })
          }}
        >
          {" "}
          Confirm
        </span>
      ),
    },
    // {
    //   key: "2",
    //   label: (
    //     <Link to={`/dashboard/confirmation/view-response/${data.personKey}`}>
    //       View Response
    //     </Link>
    //   ),
    // },
  ]
  const rowSelection = {
    selectedRowKeys: selectedPersons,
    onChange: (selectedRowKeys: React.Key[], selectedRows: Person[]) => {
      setSelectedPersons(selectedRowKeys)
      // keep the records of people picked on previous pages, since antd only
      // hands back the rows of the page currently rendered
      setSelectedPeople((prev) => {
        const known = new Map<React.Key, Person>()
        prev.forEach((person) => known.set(person.person_id, person))
        selectedRows.forEach(
          (person) => person && known.set(person.person_id, person)
        )
        return selectedRowKeys
          .map((key) => known.get(key))
          .filter((person): person is Person => Boolean(person))
      })
      setShowConfirmBtn(selectedRowKeys.length > 0)
    },
  }
  const handleSelected = () => {
    if (activeConfirmEmp.fullName !== "") {
      confirmSelected({
        person_list: [String(activeConfirmEmp.person_id)],
        send_email: "yes",
      })
    } else {
      const body = {
        person_list: selectedPersons.map((id) => id.toString()),
        send_email: "yes",
      }
      confirmSelected(body)
    }
  }
  return (
    <div className="relative !font-avenir text-[#42526D]">
      <TopActionBar>
        <div className="flex justify-between w-full">
          <div className="flex gap-2 items-center">
            <Input
              placeholder="Search"
              prefix={
                <MagnifyingGlassIcon className="w-4 h-4 text-neutral-300" />
              }
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="font-avenir "
            />
            <Select
              showSearch
              onChange={(selected) => {
                if (selected) {
                  const depnt = departmentData?.find(
                    (department) => department.text === selected
                  )
                  patchQueryExtraParams({
                    departmentId: depnt?.id ?? "",
                  })
                } else {
                  patchQueryExtraParams({
                    departmentId: "",
                  })
                }
              }}
              loading={isDepartmentDataLoading}
              filterOption={(input, option) => {
                if (option) {
                  const value = (option.value as string) || ""
                  return value.toLowerCase().indexOf(input.toLowerCase()) >= 0
                } else {
                  return false
                }
              }}
              placeholder="Filter by department"
            >
              {departmentData
                ?.map((department) => ({
                  label: department.text,
                  value: department.text,
                }))
                ?.map((option, index) => (
                  <Select.Option
                    key={index}
                    value={option.value}
                  >
                    {option.label}
                  </Select.Option>
                ))}
            </Select>
          </div>

          {/* <div className="flex gap-8">
            <Button
              color="primary"
              title="Confirm all"
            >
              Confirm all employees
            </Button>
          </div> */}
        </div>
        {/* Table */}
      </TopActionBar>

      <DashboardContainer>
        <div className="flex flex-col h-full">
          <div className="flex items-center  justify-between mb-4">
            <H6 className="">Confirmation</H6>
            <div className="flex ">
              {showConfirmBtn && (
                <Button
                  color="primary"
                  title="Confirm all"
                  onClick={() => setOpenReviewDrawer(true)}
                >
                  Confirm selected employees
                </Button>
              )}
            </div>
          </div>
          <AppTable
            columns={TableColumns}
            dataSource={confirmationRes?.data?.docs || []}
            loading={
              isLoadingConfirmation || isDepartmentDataLoading || isPreviousData
            }
            className="text-[#42526D] min-h-screen"
            rowKey="person_id"
            pagination={tablePagination}
            rowSelection={{
              type: "checkbox",
              ...rowSelection,
            }}
            rowClassName={"[&_>_*]:!font-avenir text-[#42526D]"}
          />
        </div>
      </DashboardContainer>
      <CustomModal
        open={openConfirmationModal}
        title={
          activeConfirmEmp.fullName !== ""
            ? `Confirm ${activeConfirmEmp.fullName}?`
            : "Employee confirmation"
        }
        footer={
          <div className=" w-full  flex gap-4  justify-end items-center">
            <Button
              color="secondary"
              onClick={() => {
                setOpenConfirmationModal(false)
                setActiveConfirmEmp({
                  fullName: "",
                  person_id: "",
                })
              }}
            >
              Cancel
            </Button>

            <Button
              color="primary"
              onClick={() => {
                handleSelected()
              }}
              submitting={isLoadingConfirmSelected}
            >
              Confirm
            </Button>
          </div>
        }
        // subTitle=""
        onCancel={() => {
          setOpenConfirmationModal(false)
          setActiveConfirmEmp({
            fullName: "",
            person_id: "",
          })
        }}
      >
        <div className=" flex flex-col">
          <p className="text-sm text-neutral500 ">
            Once you confirm this employee. Their profile will be updated in the
            system, ensuring seamless access to company resources and
            privileges.
          </p>
        </div>
      </CustomModal>

      <ConfirmationReviewDrawer
        open={openReviewDrawer}
        people={selectedPeople}
        isConfirming={isLoadingConfirmSelected}
        onClose={() => setOpenReviewDrawer(false)}
        onConfirm={() => handleSelected()}
      />
    </div>
  )
}

export default Confirmation